import { RefreshCw, TriangleAlert } from 'lucide-react';
import type { ReactElement } from 'react';

import { useToast } from '@/components/ui/toast';
import { usePermission } from '@/hooks/usePermission';
import { ApiError, apiErrorReason } from '@/lib/api';

import type { TrainingRow } from '../types';
import { useRetryDispatch } from '../use-training';

const PERMISSION = 'quality:training:dispatch';

function retryErrorCopy(err: Error): string {
  if (err instanceof ApiError && err.status === 409) {
    return apiErrorReason(err) === 'dispatch_in_progress'
      ? 'El envío sigue en proceso automático; vuelve a intentarlo en unos minutos'
      : 'El envío ya no está marcado como fallido; la lista se actualizó';
  }
  return 'No se pudo reprogramar el envío. Vuelve a intentarlo';
}

/** Aviso del último envío de capacitación fallido de un documento, con el reintento
 *  para quien tiene el permiso. El envío se procesa en el siguiente minuto. */
export function DispatchRetry({
  entryId,
  code,
  dispatch,
}: {
  entryId: string;
  code: string;
  dispatch: TrainingRow['dispatch'];
}): ReactElement | null {
  const canRetry = usePermission(PERMISSION);
  const retry = useRetryDispatch(entryId);
  const { showToast } = useToast();

  if (dispatch?.state !== 'failed') {
    return null;
  }

  const onRetry = (): void => {
    retry.mutate(undefined, {
      onSuccess: () => showToast(`Envío de la capacitación de ${code} reprogramado`),
      onError: (err) => showToast(retryErrorCopy(err), 'error'),
    });
  };

  return (
    <div className="cb rowb" style={{ gap: 12 }} role="alert">
      <span aria-hidden="true" style={{ color: 'var(--danger)', display: 'flex' }}>
        <TriangleAlert />
      </span>
      <div>
        <h4 style={{ fontSize: 13.5, marginBottom: 2 }}>
          El envío de la capacitación de la versión {dispatch.approvedVersion} al LMS falló.
        </h4>
        <p className="soft" style={{ fontSize: 12.5, margin: 0 }}>
          {canRetry
            ? 'Corrige la causa en el LMS y reprograma el envío; se procesa en el siguiente minuto.'
            : 'El Administrador de Calidad puede reintentarlo.'}
        </p>
      </div>
      {canRetry && (
        <button
          type="button"
          className="btn sm"
          style={{ marginLeft: 'auto' }}
          disabled={retry.isPending}
          onClick={onRetry}
        >
          <RefreshCw aria-hidden="true" />
          {retry.isPending ? 'Reintentando…' : 'Reintentar envío'}
        </button>
      )}
    </div>
  );
}
